import { LOADOUT_SLOTS, type LoadoutSlot, type SharedLoadout, type SharedLoadoutItem } from './loadout-share';

/**
 * Loadout intel (website roadmap S17 — SR Tools' Loadout Intel and Weapon Catalog, Torn Intel's enemy loadouts): the
 * loadouts Omni-Torn users saw in their fights (terms 3: every Omni-Torn user; a month, apps/api/src/faction-loadouts-seen.ts)
 * searched by player, name or faction and filtered by weapon, armor, bonus, slot, rarity and age; each player's changes
 * over the month; every weapon seen with the bonuses seen on it.
 *
 * - Only Omni-Torn users (invite-only reads) — the same people terms 3 shares these with. A "Share" link is a link to the
 *   website's page: it opens only for someone signed in to Omni-Torn (no public links — that would need terms 10).
 * - Captured only on the attack page the player was viewing (the extension, features/shared/loadout-capture.ts).
 * Pure helpers here, used by the server (search, history, catalog) and the website (cards, bonus parts).
 */

/** How far back anything is shown: the sightings are kept a month. */
export const LOADOUT_INTEL_DAYS = 30;
/** Players in one search answer. */
export const LOADOUT_SEARCH_MAX = 100;
/** Changes kept per player (and group) — the newest. */
export const LOADOUT_HISTORY_MAX = 20;
/** Weapons in one catalog answer. */
export const WEAPON_CATALOG_MAX = 500;

export const WEAPON_SLOTS: readonly LoadoutSlot[] = ['Primary', 'Secondary', 'Melee', 'Temporary'];
export const ARMOR_SLOTS: readonly LoadoutSlot[] = ['Helmet', 'Body', 'Gloves', 'Pants', 'Boots'];
export const isWeaponSlot = (s: LoadoutSlot): boolean => WEAPON_SLOTS.includes(s);

export type Rarity = NonNullable<SharedLoadoutItem['rarity']>;
export const RARITIES: readonly Rarity[] = ['yellow', 'orange', 'red'];

/** What to look for. Text matches are any part of the name, any case. */
export interface LoadoutFilter {
  /** A weapon's name (any weapon slot, or `slot` when it's a weapon slot). */
  weapon?: string;
  /** An armor piece's name. */
  armor?: string;
  /** A bonus's name ("Expose", "Bleed"…), on any item. */
  bonus?: string;
  /** Only an item in this slot counts for weapon/armor/bonus/rarity; alone: they had something in it. */
  slot?: LoadoutSlot;
  /** At least this rarity (yellow < orange < red) on a matching item. */
  rarity?: Rarity;
  /** Seen in the last this many days (1–30). */
  days?: number;
}

const text = (x: unknown, max = 40): string | undefined => {
  if (typeof x !== 'string') return undefined;
  const t = x.trim().replace(/\s+/g, ' ').slice(0, max);
  return t || undefined;
};

/** A filter from an address's query (or any object of strings) — only known fields, sane values. */
export function cleanLoadoutFilter(get: (name: string) => string | null | undefined): LoadoutFilter {
  const f: LoadoutFilter = {};
  const weapon = text(get('weapon'));
  const armor = text(get('armor'));
  const bonus = text(get('bonus'));
  const slot = get('slot');
  const rarity = get('rarity');
  const days = Number(get('days'));
  if (weapon) f.weapon = weapon;
  if (armor) f.armor = armor;
  if (bonus) f.bonus = bonus;
  if (slot && (LOADOUT_SLOTS as readonly string[]).includes(slot)) f.slot = slot as LoadoutSlot;
  if (rarity && (RARITIES as readonly string[]).includes(rarity)) f.rarity = rarity as Rarity;
  if (Number.isInteger(days) && days >= 1 && days < LOADOUT_INTEL_DAYS) f.days = days;
  return f;
}

/** The filter as query parameters (empty ones left out) — the page's address and the server call. */
export function loadoutFilterQuery(f: LoadoutFilter): Record<string, string> {
  const q: Record<string, string> = {};
  if (f.weapon) q.weapon = f.weapon;
  if (f.armor) q.armor = f.armor;
  if (f.bonus) q.bonus = f.bonus;
  if (f.slot) q.slot = f.slot;
  if (f.rarity) q.rarity = f.rarity;
  if (f.days) q.days = String(f.days);
  return q;
}

export const hasItemFilter = (f: LoadoutFilter): boolean => !!(f.weapon || f.armor || f.bonus || f.slot || f.rarity);

const has = (name: string, part: string): boolean => name.toLowerCase().includes(part.toLowerCase());

/** One bonus as shown ("Expose 7%", "Disarm 5 turns", "Blindfire") → its name, number and unit. */
export function bonusParts(b: string): { name: string; value: number | null; unit: '%' | 'turns' | '' } {
  const m = /^(.*?)\s+(\d+(?:\.\d+)?)\s*(%|turns?)?$/i.exec(b.trim());
  if (!m) return { name: b.trim(), value: null, unit: '' };
  return { name: m[1]!.trim(), value: Number(m[2]), unit: m[3] ? (m[3] === '%' ? '%' : 'turns') : '' };
}

/** Does this loadout match the filter's item parts (weapon, armor, bonus, slot, rarity)? Age is checked by the caller. */
export function loadoutMatches(l: SharedLoadout, f: LoadoutFilter): boolean {
  const inSlot = (i: SharedLoadoutItem) => !f.slot || i.slot === f.slot;
  const rare = (i: SharedLoadoutItem) => !f.rarity || (!!i.rarity && RARITIES.indexOf(i.rarity) >= RARITIES.indexOf(f.rarity));
  const bonus = (i: SharedLoadoutItem) => !f.bonus || (i.bonuses ?? []).some((b) => has(bonusParts(b).name, f.bonus!));
  const items = l.items.filter(inSlot);
  if (f.slot && !items.length) return false;
  if (f.weapon && !items.some((i) => isWeaponSlot(i.slot) && has(i.name, f.weapon!) && rare(i) && bonus(i))) return false;
  if (f.armor && !items.some((i) => !isWeaponSlot(i.slot) && has(i.name, f.armor!) && rare(i) && bonus(i))) return false;
  if (!f.weapon && !f.armor && (f.bonus || f.rarity) && !items.some((i) => rare(i) && bonus(i))) return false;
  return true;
}

/** One item's identity and numbers (two loadouts are the same when every slot's key is). */
const itemKey = (i: SharedLoadoutItem): string => `${i.slot}:${i.itemId}:${i.damage ?? ''}:${i.accuracy ?? ''}:${i.armor ?? ''}:${(i.bonuses ?? []).join('+')}:${i.rarity ?? ''}`;

/** A loadout's identity: its items in slot order (drawing details left out). */
export function loadoutKey(l: SharedLoadout): string {
  return [...l.items]
    .sort((a, b) => LOADOUT_SLOTS.indexOf(a.slot) - LOADOUT_SLOTS.indexOf(b.slot))
    .map(itemKey)
    .join('|');
}

/** The slots that differ from `before` to `after` (something new, gone, or another copy). */
export function changedSlots(before: SharedLoadout | null, after: SharedLoadout): LoadoutSlot[] {
  if (!before) return [];
  const a = new Map(before.items.map((i) => [i.slot, itemKey(i)]));
  const b = new Map(after.items.map((i) => [i.slot, itemKey(i)]));
  return LOADOUT_SLOTS.filter((s) => a.get(s) !== b.get(s));
}

// ---------------------------------------------------------------- answers

/** One player's newest loadout seen in a fight. */
export interface LoadoutSighting {
  id: number;
  /** Their name as the attack page showed it ('' when it didn't). */
  name: string;
  /** When it was seen (ms). */
  at: number;
  loadout: SharedLoadout;
}

/** GET /api/loadouts/search */
export interface LoadoutSearchAnswer {
  rows: LoadoutSighting[];
  /** More matched than LOADOUT_SEARCH_MAX (or than one search reads). */
  more: boolean;
  /** A faction search: the faction (Torn's public members list, read on demand and kept an hour). */
  faction?: { id: number; name: string; members: number; at: number; calls: number };
}

/** One loadout a player wore, from first to last seen. */
export interface LoadoutChange {
  /** First and last seen wearing it (ms). */
  from: number;
  to: number;
  /** Fights it was seen in (sightings). */
  seen: number;
  loadout: SharedLoadout;
  /** What changed from the one before (empty for the oldest). */
  changed: LoadoutSlot[];
}

/** GET /api/loadouts/player/:id — newest first. */
export interface LoadoutHistoryAnswer {
  id: number;
  name: string;
  changes: LoadoutChange[];
}

/** A bonus seen on a weapon: how often, and its lowest and highest number. */
export interface CatalogBonus {
  name: string;
  count: number;
  min: number | null;
  max: number | null;
  unit: '%' | 'turns' | '';
}

export interface CatalogRange {
  min: number;
  max: number;
  avg: number;
}

/** One weapon (by item id) across every newest loadout seen. */
export interface CatalogWeapon {
  itemId: number;
  name: string;
  slot: LoadoutSlot;
  /** Players seen with it (their newest loadout). */
  players: number;
  damage: CatalogRange | null;
  accuracy: CatalogRange | null;
  bonuses: CatalogBonus[];
  rarity: Record<Rarity | 'plain', number>;
  /** Newest sighting with it (ms). */
  lastAt: number;
}

/** GET /api/loadouts/catalog */
export interface WeaponCatalogAnswer {
  /** When it was worked out (kept a few minutes). */
  at: number;
  /** Players whose newest loadout went into it. */
  players: number;
  weapons: CatalogWeapon[];
}

const range = (xs: number[]): CatalogRange | null => (xs.length ? { min: Math.min(...xs), max: Math.max(...xs), avg: Math.round((xs.reduce((a, b) => a + b, 0) / xs.length) * 100) / 100 } : null);

/**
 * Every weapon in these loadouts (one per player — their newest), the most-seen first: how many carry it, damage and
 * accuracy ranges, the bonuses seen on it (how often, lowest/highest), and its rarities. No players named.
 */
export function weaponCatalog(rows: ReadonlyArray<Pick<LoadoutSighting, 'at' | 'loadout'>>, now: number, max = WEAPON_CATALOG_MAX): WeaponCatalogAnswer {
  const acc = new Map<number, { name: string; slot: LoadoutSlot; players: number; damage: number[]; accuracy: number[]; bonuses: Map<string, CatalogBonus>; rarity: CatalogWeapon['rarity']; lastAt: number }>();
  for (const r of rows) {
    for (const i of r.loadout.items) {
      if (!isWeaponSlot(i.slot)) continue;
      let w = acc.get(i.itemId);
      if (!w) acc.set(i.itemId, (w = { name: i.name, slot: i.slot, players: 0, damage: [], accuracy: [], bonuses: new Map(), rarity: { plain: 0, yellow: 0, orange: 0, red: 0 }, lastAt: 0 }));
      w.players++;
      if (typeof i.damage === 'number') w.damage.push(i.damage);
      if (typeof i.accuracy === 'number') w.accuracy.push(i.accuracy);
      w.rarity[i.rarity ?? 'plain']++;
      if (r.at > w.lastAt) {
        w.lastAt = r.at;
        w.name = i.name;
      }
      for (const b of i.bonuses ?? []) {
        const p = bonusParts(b);
        const key = p.name.toLowerCase();
        let c = w.bonuses.get(key);
        if (!c) w.bonuses.set(key, (c = { name: p.name, count: 0, min: null, max: null, unit: p.unit }));
        c.count++;
        if (p.value !== null) {
          c.min = c.min === null ? p.value : Math.min(c.min, p.value);
          c.max = c.max === null ? p.value : Math.max(c.max, p.value);
        }
      }
    }
  }
  const weapons = [...acc.entries()]
    .map(([itemId, w]): CatalogWeapon => ({ itemId, name: w.name, slot: w.slot, players: w.players, damage: range(w.damage), accuracy: range(w.accuracy), bonuses: [...w.bonuses.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)), rarity: w.rarity, lastAt: w.lastAt }))
    .sort((a, b) => b.players - a.players || a.name.localeCompare(b.name))
    .slice(0, max);
  return { at: now, players: rows.length, weapons };
}
